
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

interface Reel {
  id: string;
  title: string;
  title_en?: string;
  title_es?: string;
  title_pt?: string;
  description?: string;
  description_en?: string;
  description_es?: string;
  description_pt?: string;
  thumbnailUrl: string;
  videoUrl: string;
  likes: number;
}

interface ReelsTabProps {
  session: any;
}

const ReelsTab: React.FC<ReelsTabProps> = ({ session }) => {
  const navigate = useNavigate();
  const [reels, setReels] = useState<Reel[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const { i18n, t } = useTranslation();
  
  useEffect(() => {
    const fetchReels = async () => {
      try {
        setLoading(true);
        
        const { data: dbReels, error } = await supabase
          .from('education_reels')
          .select('*')
          .returns<any[]>();
          
        if (error) {
          throw error;
        }
        
        if (dbReels && dbReels.length > 0) {
          // Procesar reels desde la base de datos
          const processedReels: Reel[] = dbReels.map((reel: any) => {
            const currentLang = i18n.language;
            // Get title in current language or fallback to default title
            const title = reel[`title_${currentLang}`] || reel.title;
            // Get description in current language or fallback to default description
            const description = reel[`description_${currentLang}`] || reel.description;
            
            return {
              id: reel.id,
              title,
              description,
              thumbnailUrl: reel.thumbnail_url || 'https://images.unsplash.com/photo-1512621776951-a57141f2eefd?q=80&w=200&auto=format',
              videoUrl: reel.video_url,
              likes: Math.floor(Math.random() * 500) // Mantener los likes aleatorios para demo
            };
          });
          
          setReels(processedReels);
        } else {
          // Si no hay reels en la base de datos, usar los de respaldo
          const backupReels: Reel[] = [
            {
              id: '1',
              title: 'Cómo preparar una ensalada rica en hierro',
              thumbnailUrl: 'https://images.unsplash.com/photo-1512621776951-a57141f2eefd?q=80&w=200&auto=format',
              videoUrl: 'https://www.example.com/video1.mp4',
              likes: 342
            },
            {
              id: '2',
              title: 'Reconoce los síntomas de la anemia',
              thumbnailUrl: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?q=80&w=200&auto=format',
              videoUrl: 'https://www.example.com/video2.mp4',
              likes: 156
            },
            {
              id: '3',
              title: 'Dieta completa para prevenir la anemia',
              thumbnailUrl: 'https://images.unsplash.com/photo-1498837167922-ddd27525d352?q=80&w=200&auto=format',
              videoUrl: 'https://www.example.com/video3.mp4',
              likes: 278
            },
            {
              id: '4',
              title: 'Alimentos ricos en hierro de origen vegetal',
              thumbnailUrl: 'https://images.unsplash.com/photo-1610348725531-843dff563e2c?q=80&w=200&auto=format',
              videoUrl: 'https://www.example.com/video4.mp4',
              likes: 198
            },
            {
              id: '5',
              title: 'Ejercicios recomendados para personas con anemia',
              thumbnailUrl: 'https://images.unsplash.com/photo-1591311630200-ffa9120a540f?q=80&w=200&auto=format',
              videoUrl: 'https://www.example.com/video5.mp4',
              likes: 412
            }
          ];
          setReels(backupReels);
        }
      } catch (error) {
        console.error("Error fetching reels:", error);
        toast.error("Error al cargar los videos educativos");
      } finally {
        setLoading(false);
      }
    };
    
    fetchReels();
  }, [i18n.language]);
  
  const handleReelClick = (reelId: string) => {
    navigate(`/reel/${reelId}`);
  };
  
  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <Loader2 className="w-6 h-6 text-sangre-500 animate-spin" />
        <span className="ml-2">{t('loadingVideos')}</span>
      </div>
    );
  }
  
  return (
    <div className="animate-fade-in">
      <h3 className="text-lg font-medium text-gray-800 mb-3">{t('informationalVideos')}</h3>
      
      <div className="grid grid-cols-2 gap-2">
        {reels.map(reel => (
          <div 
            key={reel.id} 
            className="relative cursor-pointer hover:opacity-90 transition-opacity duration-200 border border-gray-200 rounded-md overflow-hidden"
            onClick={() => handleReelClick(reel.id)}
          >
            <div className="aspect-[9/16] overflow-hidden bg-black">
              <img 
                src={reel.thumbnailUrl} 
                alt={reel.title} 
                className="w-full h-full object-cover opacity-80"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent flex flex-col justify-end p-3">
                <h4 className="font-medium text-white text-sm">{reel.title}</h4>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {reels.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          <p>{t('noVideosAvailable')}</p>
        </div>
      )}
      
      {session ? (
        <div className="mt-6 bg-white p-4 rounded-xl border border-gray-100 shadow-sm text-center">
          <p className="text-gray-600 mb-2">{t('resultsInProfile')}</p>
          <Button 
            onClick={() => navigate('/profile')}
            variant="outline"
            className="mt-2"
          >
            {t('viewHistory')}
          </Button>
        </div>
      ) : null}
    </div>
  );
};

export default ReelsTab;
